import { Radii } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { useMultiStyleConfig } from '~/utils/styleHelpers'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

const BackgroundColor = 'bg.muted'
const TextColor = 'fg.default'
const OnTextColor = 'bg.panel'
const ReceivingCallsIconColor = 'green.fg'

export const DoNotDisturbSplitButtonStyleValues = {
  menuWidth: '19.5rem',
}

export enum DoNotDisturbSplitButtonVariants {
  DoNotDisturb = 'doNotDisturb',
}

const DoNotDisturbSplitButtonStyleParts = [
  'container',
  'leftButton',
  'rightButton',
  'leftButtonTextWrapper',
  'titleText',
  'subtitleText',
  'doNotDisturbIcon',
  'chevronIcon',
] as const

const helpers = createMultiStyleConfigHelpers(DoNotDisturbSplitButtonStyleParts)

export const DoNotDisturbSplitButtonStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      position: 'relative',
    },
    leftButtonTextWrapper: {
      display: 'flex',
      position: 'relative',
      flexDirection: 'column',
      marginLeft: '0.5rem',
    },
    leftButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '2.5rem',
      width: '10.25rem',
      textAlign: 'start',
      borderTopLeftRadius: Radii.sm,
      borderBottomLeftRadius: Radii.sm,
      backgroundColor: BackgroundColor,
      transition: 'all 0.15s ease-in-out',
      cursor: 'pointer',
      userSelect: 'none',
      _hover: {
        backgroundColor: 'bg.emphasized',
      },
      _active: {
        backgroundColor: 'bg.emphasized',
      },
      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'brand.solid',
        outlineOffset: '2px',
      },
    },
    rightButton: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '2.5rem',
      width: '3rem',
      marginLeft: '1px',
      borderTopRightRadius: Radii.sm,
      borderBottomRightRadius: Radii.sm,
      transition: 'all 0.15s ease-in-out',
      cursor: 'pointer',
      userSelect: 'none',
      backgroundColor: BackgroundColor,
      _hover: {
        backgroundColor: 'bg.emphasized',
      },
      _active: {
        backgroundColor: 'bg.emphasized',
      },
    },
    titleText: {
      color: TextColor,
      ...textStyles[textStyleKeys.Body2],
      lineHeight: '0.875rem',
    },
    subtitleText: {
      color: TextColor,
      ...textStyles[textStyleKeys.Body3],
      lineHeight: '0.875rem',
    },
    doNotDisturbIcon: {
      height: '1.125rem',
      width: '1.125rem',
      color: ReceivingCallsIconColor,
    },
    chevronIcon: {
      boxSize: '1rem',
      color: TextColor,
    },
  },
  variants: {
    [DoNotDisturbSplitButtonVariants.DoNotDisturb]: {
      leftButton: {
        backgroundColor: 'red.solid',
        _hover: {
          backgroundColor: 'red.focusRing',
          color: 'red.contrast',
        },
        _active: {
          backgroundColor: 'red.focusRing',
          color: 'red.contrast',
        },
      },
      rightButton: {
        backgroundColor: 'red.solid',
        _hover: {
          backgroundColor: 'red.focusRing',
          color: 'red.contrast',
        },
        _active: {
          backgroundColor: 'red.focusRing',
          color: 'red.contrast',
        },
      },
      titleText: {
        ...textStyles[textStyleKeys.Body2_Semibold],
        color: OnTextColor,
      },
      subtitleText: {
        color: OnTextColor,
      },
      doNotDisturbIcon: {
        color: OnTextColor,
      },
      chevronIcon: {
        color: OnTextColor,
      },
    },
  },
})

export const useDoNotDisturbSplitButtonStyle = (props?: StyleThemingProps) => {
  return useMultiStyleConfig(
    DoNotDisturbSplitButtonStyle,
    props,
  ) as MultiStyleType<typeof DoNotDisturbSplitButtonStyleParts>
}
